import React from 'react';
import { Box, Typography, Button, keyframes } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

// SVG Illustrations as components
function RecipeBookIllustration({ color }) {
  return (
    <svg viewBox="0 0 120 100" width="120" height="100">
      {/* Book */}
      <rect x="20" y="20" width="80" height="60" rx="4" fill={color} opacity="0.15" />
      <rect x="25" y="25" width="70" height="50" rx="2" fill={color} opacity="0.25" />
      <line x1="60" y1="25" x2="60" y2="75" stroke={color} strokeWidth="2" opacity="0.4" />
      
      {/* Lines on pages */}
      <line x1="30" y1="35" x2="55" y2="35" stroke={color} strokeWidth="2" opacity="0.3" />
      <line x1="30" y1="45" x2="50" y2="45" stroke={color} strokeWidth="2" opacity="0.3" />
      <line x1="30" y1="55" x2="52" y2="55" stroke={color} strokeWidth="2" opacity="0.3" />
      <line x1="65" y1="35" x2="90" y2="35" stroke={color} strokeWidth="2" opacity="0.3" />
      <line x1="65" y1="45" x2="85" y2="45" stroke={color} strokeWidth="2" opacity="0.3" />
      <line x1="65" y1="55" x2="88" y2="55" stroke={color} strokeWidth="2" opacity="0.3" />
      
      {/* Sparkles */}
      <circle cx="15" cy="15" r="3" fill={color}>
        <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="105" cy="25" r="2" fill={color}>
        <animate attributeName="opacity" values="0.5;1;0.5" dur="1.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="100" cy="80" r="2.5" fill={color}>
        <animate attributeName="opacity" values="0.4;1;0.4" dur="1.8s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

function EmptyBowlIllustration({ color }) {
  return (
    <svg viewBox="0 0 120 100" width="120" height="100">
      {/* Bowl */}
      <ellipse cx="60" cy="60" rx="45" ry="20" fill={color} opacity="0.15" />
      <path 
        d="M15 50 Q15 80 60 80 Q105 80 105 50" 
        fill="none" 
        stroke={color} 
        strokeWidth="3" 
        opacity="0.4"
      />
      <ellipse cx="60" cy="50" rx="45" ry="15" fill={color} opacity="0.1" />
      
      {/* Steam lines */}
      <path d="M45 35 Q42 25 45 15" fill="none" stroke={color} strokeWidth="2" opacity="0.3">
        <animate attributeName="d" values="M45 35 Q42 25 45 15;M45 35 Q48 25 45 15;M45 35 Q42 25 45 15" dur="2s" repeatCount="indefinite" />
      </path>
      <path d="M60 30 Q57 20 60 10" fill="none" stroke={color} strokeWidth="2" opacity="0.3">
        <animate attributeName="d" values="M60 30 Q57 20 60 10;M60 30 Q63 20 60 10;M60 30 Q57 20 60 10" dur="2.5s" repeatCount="indefinite" />
      </path>
      <path d="M75 35 Q72 25 75 15" fill="none" stroke={color} strokeWidth="2" opacity="0.3">
        <animate attributeName="d" values="M75 35 Q72 25 75 15;M75 35 Q78 25 75 15;M75 35 Q72 25 75 15" dur="1.8s" repeatCount="indefinite" />
      </path>
    </svg>
  );
}

function GroceryBagIllustration({ color }) {
  return (
    <svg viewBox="0 0 120 100" width="120" height="100">
      {/* Bag */}
      <path 
        d="M30 35 L30 85 Q30 90 35 90 L85 90 Q90 90 90 85 L90 35 Z" 
        fill={color} 
        opacity="0.15" 
      />
      <path 
        d="M35 35 Q35 20 60 20 Q85 20 85 35" 
        fill="none" 
        stroke={color} 
        strokeWidth="3" 
        opacity="0.4"
      />
      
      {/* Items peeking out */}
      <ellipse cx="45" cy="35" rx="8" ry="12" fill={color} opacity="0.3" />
      <ellipse cx="60" cy="30" rx="6" ry="10" fill={color} opacity="0.25" />
      <ellipse cx="75" cy="35" rx="7" ry="11" fill={color} opacity="0.3" />
      
      {/* Carrot top */}
      <path d="M48 25 L45 15 M48 25 L50 18 M48 25 L52 20" stroke={color} strokeWidth="2" opacity="0.4" />
      
      {/* Checkmark */}
      <circle cx="90" cy="20" r="12" fill={color} opacity="0.2" />
      <path d="M84 20 L88 24 L96 16" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Empty State Components
export function EmptyListsState({ onCreateList }) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box sx={{ animation: `${float} 3s ease-in-out infinite`, mb: 2 }}>
        <RecipeBookIllustration color={primaryColor} />
      </Box>
      
      <Typography 
        variant="h6" 
        sx={{ 
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.primary,
          mb: 1,
        }}
      >
        No lists yet
      </Typography>
      
      <Typography 
        variant="body2" 
        sx={{ 
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.secondary,
          mb: 3,
          maxWidth: 280,
        }}
      >
        Create your first list to organize your favorite recipes
      </Typography>
      
      {onCreateList && (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onCreateList}
          sx={{
            fontFamily: theme.typography.fontFamily,
            textTransform: 'none',
            animation: `${bounce} 2s ease-in-out infinite`,
            animationDelay: '1s',
          }}
        >
          Create your first list
        </Button>
      )}
    </Box>
  );
}

export function EmptyRecipesInListState({ onBrowseRecipes }) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box sx={{ animation: `${float} 3s ease-in-out infinite`, mb: 2 }}>
        <EmptyBowlIllustration color={primaryColor} />
      </Box>
      
      <Typography 
        variant="h6" 
        sx={{ 
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.primary,
          mb: 1,
        }}
      >
        This list is empty
      </Typography>
      
      <Typography 
        variant="body2" 
        sx={{ 
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.secondary,
          mb: 3,
          maxWidth: 280,
        }}
      >
        Add some recipes to get started with meal planning
      </Typography>
      
      {onBrowseRecipes && (
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={onBrowseRecipes}
          sx={{
            fontFamily: theme.typography.fontFamily,
            textTransform: 'none',
          }}
        >
          Browse recipes
        </Button>
      )}
    </Box>
  );
}

export function ShoppingListCompleteState() {
  const theme = useTheme();
  const successColor = theme.palette.success?.main || '#4caf50';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box sx={{ animation: `${float} 3s ease-in-out infinite`, mb: 2 }}>
        <GroceryBagIllustration color={successColor} />
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CheckCircleIcon sx={{ color: successColor }} />
        <Typography 
          variant="h6" 
          sx={{ 
            fontFamily: theme.typography.fontFamily,
            color: theme.palette.text.primary,
          }}
        >
          All done!
        </Typography>
      </Box>
      
      <Typography 
        variant="body2" 
        sx={{ 
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.secondary,
          maxWidth: 280,
        }}
      >
        You've checked off everything on your list
      </Typography>
    </Box>
  );
}

export function NoSearchResultsState({ searchTerm }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box 
        sx={{ 
          width: 80, 
          height: 80, 
          borderRadius: '50%', 
          backgroundColor: theme.palette.action.hover,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        <SearchIcon sx={{ fontSize: 40, color: theme.palette.text.secondary }} />
      </Box>
      
      <Typography 
        variant="h6" 
        sx={{ 
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.primary,
          mb: 1,
        }}
      >
        No lists found
      </Typography>
      
      <Typography 
        variant="body2" 
        sx={{ 
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.secondary,
          maxWidth: 280,
        }}
      >
        {searchTerm 
          ? `No lists matching "${searchTerm}"` 
          : 'Try a different search term'
        }
      </Typography>
    </Box>
  );
}

